import React, { useState } from 'react';

import { notify } from '@NOTIFICATION/Notificator';

import { EntityTable, EntityFormModal } from '@COMPONENTS/SHARED/EntityManagement';

import {
    useGetAllRegionsAdmin,
    useCreateRegion,
    useUpdateRegion,
    useDeleteRegion,
} from '@SERVICES';
import { Region } from '@INTERFACES/api/regions';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function RegionsTab() {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingRegion, setEditingRegion] = useState<Region | null>(null);

    const { data: regions = [], isLoading, refetch } = useGetAllRegionsAdmin();
    const [createRegion, { isLoading: isCreating }] = useCreateRegion();
    const [updateRegion, { isLoading: isUpdating }] = useUpdateRegion();
    const [deleteRegion] = useDeleteRegion();

    const columns = [
        {
            key: 'id' as keyof Region,
            header: 'ID',
        },
        {
            key: 'name' as keyof Region,
            header: 'Name',
        },
    ];

    const formFields = [
        {
            key: 'name',
            label: 'Name',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter region name',
        },
    ];

    const handleCreate = () => {
        setEditingRegion(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (region: Region) => {
        setEditingRegion(region);
        setIsFormModalOpen(true);
    };

    const handleDelete = async (region: Region) => {
        if (window.confirm(`Are you sure you want to delete "${region.name}"?`)) {
            try {
                await deleteRegion(region.id).unwrap();
                notify.success('Region deleted successfully!');
                refetch();
            } catch (error) {
                // Extract specific error message from API response
                let errorMessage = 'Failed to delete region!';
                
                if (error && typeof error === 'object' && 'status' in error) {
                    const fetchError = error as FetchBaseQueryError;
                    if (typeof fetchError.status === 'number' && fetchError.data) {
                        // Try to extract the message from the API response
                        const errorData = fetchError.data as any;
                        if (errorData && typeof errorData === 'object' && errorData.message) {
                            errorMessage = errorData.message;
                        }
                    }
                }
                
                notify.error(errorMessage);
            }
        }
    };

    const handleFormSubmit = async (formData: any) => {
        try {
            if (editingRegion) {
                await updateRegion({ id: editingRegion.id, ...formData }).unwrap();
                notify.success('Region updated successfully!');
            } else {
                await createRegion(formData).unwrap();
                notify.success('Region created successfully!');
            }
            setIsFormModalOpen(false);
            setEditingRegion(null);
            refetch();
        } catch (error) {
            notify.error(`Failed to ${editingRegion ? 'update' : 'create'} region!`);
        }
    };

    const handleFormClose = () => {
        setIsFormModalOpen(false);
        setEditingRegion(null);
    };

    return (
        <div style={{ padding: '24px' }}>
            <EntityTable
                data={regions}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                isLoading={isLoading}
                entityName="Region"
            />

            <EntityFormModal
                isOpen={isFormModalOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                title={editingRegion ? 'Edit Region' : 'Create Region'}
                fields={formFields}
                initialData={editingRegion}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}

export default RegionsTab;
